import "./index.scss";
import { GeneralSmallChevronDown } from "@wise-emotions/react-icons";
import { GetTranslationsObj } from "types/types";
import { HeaderSubmenuItem } from "components/HeaderNew/HeaderFull/types";
import { MenuOpeningStatus } from "components/HeaderNew/HeaderFull";
import { Poly } from "components/Poly";
import { useOnClickOutside } from "@wise-emotions/react-hooks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SubmenuDropdown from "components/HeaderNew/components/SubmenuDropdown";
import cn from "classnames";

type Props = {
  isActive?: boolean;
  submenu: HeaderSubmenuItem;
  isSomeDropdownOpen: boolean;
  translations: GetTranslationsObj;
  setIsSomeDropdownOpen: (value: boolean) => void;
  onClick?: (menuItem: MenuOpeningStatus) => void;
};

const SubmenuButton = ({
  isActive,
  submenu,
  isSomeDropdownOpen,
  translations,
  setIsSomeDropdownOpen,
  onClick,
}: Props) => {
  const [showMobileDropdpown, setShowMobileDropdown] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef<HTMLLIElement | null>(null);

  const isMobile = !!onClick;
  const hasGroups = submenu.groups.length > 0;

  const handleClick = () => {
    if (!hasGroups) return;
    onClick?.("groupOpen");
    setShowMobileDropdown(true);
  };

  const handleMobileBackCta = () => {
    onClick?.("submenuOpen");
    setShowMobileDropdown(false);
  };

  const handleFocus = () => {
    if (isMobile) return;
    setIsSomeDropdownOpen(true);
    setShowDropdown(true);
  };

  const handleBlur = () => {
    if (isMobile || hasGroups) return;
    setIsSomeDropdownOpen(false);
    setShowDropdown(false);
  };

  const handleChildBlur = () => {
    if (isMobile) return;
    setIsSomeDropdownOpen(false);
    setShowDropdown(false);
  };

  const handleMouseEnter = () => {
    if (isMobile) return;
    setIsSomeDropdownOpen(false);
  };

  const onClickOutside = useCallback(() => {
    if (showDropdown) {
      setIsSomeDropdownOpen(false);
      setShowDropdown(false);
    }
  }, [setIsSomeDropdownOpen, showDropdown]);

  useOnClickOutside(ref, onClickOutside);

  useEffect(() => {
    if (!isMobile && !isSomeDropdownOpen) setShowDropdown(false);
  }, [isMobile, isSomeDropdownOpen]);

  return (
    <li
      ref={ref}
      className={cn("submenu-button", {
        "submenu-button--dropdown-keyboard-show":
          // during Tab navigation the dropdown will be open when not on mobile,
          // when the single SubemnuButton state is true and when the general SubmenuButtons state is true
          !isMobile && showDropdown && isSomeDropdownOpen,
      })}
    >
      <Poly
        className={cn("submenu-button__poly", {
          "submenu-button__poly--active": isActive,
        })}
        {...submenu.anchorProps}
        onFocus={handleFocus}
        onBlur={handleBlur}
        // set false the general state to close another dropdown opened during Tab navigation when focus another SubmenuButton
        onMouseEnter={handleMouseEnter}
        aria-haspopup={String(hasGroups)}
        as={onClick && hasGroups ? "button" : submenu.anchorProps.as}
        onClick={handleClick}
      >
        {submenu.label}
        {hasGroups && <GeneralSmallChevronDown />}
      </Poly>
      <div
        className={cn({
          "submenu-button__dropdown-desktop": !isMobile,
          "submenu-button__dropdown-mobile": isMobile,
          "submenu-button__dropdown-mobile--show": showMobileDropdpown,
        })}
      >
        {hasGroups && (
          <SubmenuDropdown
            {...submenu}
            isMobile={isMobile}
            translations={translations}
            onBlur={handleChildBlur}
            onClickMobileBackCta={handleMobileBackCta}
          />
        )}
      </div>
    </li>
  );
};

export default SubmenuButton;
